/* Product cards (docs/15 §10): quick add, quick add / quick view hand-off and card swatches.
   Delegated listeners on document serve every card on the page, including cards inserted later
   by section rendering or overlays, so there is no per-card setup. The server-rendered card works
   without this file: single-variant quick add posts a normal form and the title links the product.

   Events
     cart:updated   on document after a quick add; detail = cart JSON from /cart.js
     mw:cart:added  on document after a quick add; detail = { items, cart, source }
     mw:quick-add   on the trigger (bubbles, cancelable); detail = { handle, url, variantId, trigger }
     mw:quick-view  same. If no listener calls preventDefault(), the browser opens the product page.
   API
     window.Munkwell.card.add(items, source) → Promise<{ added, cart }>  (items: [{ id, quantity }]) */

const CARD = '[data-card]';
const SWATCH = '[data-card] .swatch';
const ADDED_MS = 2000;

const routes = () => window.Munkwell?.routes ?? {};
const closest = (target, selector) => (target instanceof Element ? target.closest(selector) : null);
const cardOf = (el) => el?.closest(CARD) ?? null;
const imageOf = (card) => card?.querySelector('.card__img--main') ?? null;
const linkOf = (card) => card?.querySelector('.card__link') ?? null;

/* ---------- Cart ---------- */

async function readJSON(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

async function postItems(items) {
  const response = await fetch(`${routes().cartAdd || '/cart/add'}.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify({ items }),
  });
  const data = await readJSON(response);
  if (!response.ok || data.status) {
    const error = new Error(data.message || response.statusText || 'Cart error');
    // Shopify's description is already in the storefront language ("Only 2 left", …).
    error.userMessage = typeof data.description === 'string' ? data.description : '';
    throw error;
  }
  return data;
}

async function fetchCart() {
  const response = await fetch(`${routes().cart || '/cart'}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(response.statusText);
  return response.json();
}

async function add(items, source = null) {
  const added = await postItems(items);
  let cart = null;
  try {
    cart = await fetchCart();
  } catch {
    // The item is in the cart; only the follow-up read failed. Counters refresh on the next event.
  }
  if (cart) document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
  document.dispatchEvent(
    new CustomEvent('mw:cart:added', { detail: { items: added.items ?? [added], cart, source } }),
  );
  return { added, cart };
}

/* ---------- Feedback ---------- */

let liveRegion = null;
const addedTimers = new WeakMap();

function announce(text) {
  if (!text) return;
  if (!liveRegion) {
    liveRegion = document.createElement('div');
    liveRegion.className = 'visually-hidden';
    liveRegion.setAttribute('role', 'status');
    document.body.append(liveRegion);
  }
  liveRegion.textContent = '';
  // A short delay makes screen readers treat the same message twice as a new announcement.
  setTimeout(() => {
    liveRegion.textContent = text;
  }, 100);
}

function setBusy(card, button, busy) {
  card?.classList.toggle('is-busy', busy);
  if (!button) return;
  if (busy) {
    button.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-disabled', 'true');
  } else {
    button.removeAttribute('aria-busy');
    button.removeAttribute('aria-disabled');
  }
}

function showAdded(button) {
  if (!button) return;
  button.classList.add('is-added');
  clearTimeout(addedTimers.get(button));
  addedTimers.set(
    button,
    setTimeout(() => button.classList.remove('is-added'), ADDED_MS),
  );
}

function clearError(card) {
  card?.querySelector('.card__message')?.remove();
}

function showError(card, text) {
  if (!card || !text) return;
  clearError(card);
  const message = document.createElement('p');
  message.className = 'card__message';
  message.setAttribute('role', 'alert');
  message.textContent = text;
  (card.querySelector('.card__content') ?? card).append(message);
}

/* ---------- Quick add (single variant) ---------- */

async function onQuickAddSubmit(event) {
  const form = closest(event.target, 'form[data-card-add]');
  if (!form) return;
  event.preventDefault();

  const button = form.querySelector('[type="submit"]');
  if (button?.getAttribute('aria-busy') === 'true') return;
  const card = cardOf(form);
  const id = Number(form.elements.id?.value);
  const quantity = Number(form.elements.quantity?.value) || 1;
  if (!id) return;

  clearError(card);
  setBusy(card, button, true);
  try {
    await add([{ id, quantity }], card);
    if (form.hasAttribute('data-cart-page')) {
      window.location.assign(routes().cart || '/cart');
      return;
    }
    announce(form.dataset.success);
    showAdded(button);
  } catch (error) {
    showError(card, error.userMessage || form.dataset.error);
  } finally {
    setBusy(card, button, false);
  }
}

/* ---------- Quick add (options) and quick view: hand off to an overlay, else open the product ---------- */

function handOff(trigger) {
  const card = cardOf(trigger);
  if (!card) return;
  // A trigger may carry its own product URL (cards without a .card__link, e.g. banner chips).
  const url = trigger.dataset.url || linkOf(card)?.href || '';
  const detail = {
    handle: card.dataset.handle ?? '',
    url,
    variantId: Number(card.dataset.variantId) || null,
    trigger,
  };
  const type = trigger.dataset.cardQuick === 'view' ? 'view' : 'add';
  const event = new CustomEvent(`mw:quick-${type}`, { bubbles: true, cancelable: true, detail });
  const unhandled = trigger.dispatchEvent(event);
  if (unhandled && url) window.location.assign(url);
}

/* ---------- Swatches ---------- */

const originals = new WeakMap(); // img → { src, srcset } as rendered by the server
const chosen = new WeakMap(); // card → { src, srcset } of the swatch the shopper selected

function widthsOf(img) {
  const srcset = originals.get(img)?.srcset ?? img.getAttribute('srcset') ?? '';
  return srcset
    .split(',')
    .map((candidate) => Number.parseInt(candidate.trim().split(/\s+/)[1], 10))
    .filter((width) => width > 0);
}

function sourceFor(swatch, img) {
  const image = swatch.dataset.image;
  if (!image) return null;
  const at = (width) => {
    const url = new URL(image, window.location.href);
    url.searchParams.set('width', String(width));
    return url.href;
  };
  const widths = widthsOf(img);
  if (!widths.length) return { src: at(1080), srcset: '' };
  return {
    src: at(Math.max(...widths)),
    srcset: widths.map((width) => `${at(width)} ${width}w`).join(', '),
  };
}

function apply(img, source) {
  if (!originals.has(img)) {
    originals.set(img, { src: img.getAttribute('src'), srcset: img.getAttribute('srcset') });
  }
  if (img.getAttribute('src') === source.src && (img.getAttribute('srcset') ?? '') === (source.srcset ?? '')) return;
  // The browser keeps painting the current image until the new one has loaded: no flash.
  if (source.srcset) img.setAttribute('srcset', source.srcset);
  else img.removeAttribute('srcset');
  img.setAttribute('src', source.src);
}

function preview(swatch) {
  const card = cardOf(swatch);
  const img = imageOf(card);
  if (!img) return;
  const source = sourceFor(swatch, img);
  if (!source) return;
  card.classList.add('is-previewing');
  apply(img, source);
}

function restore(card) {
  if (!card) return;
  card.classList.remove('is-previewing');
  const img = imageOf(card);
  const source = chosen.get(card) ?? (img && originals.get(img));
  if (img && source) apply(img, source);
}

function select(swatch) {
  const card = cardOf(swatch);
  if (!card) return;
  swatch
    .closest('[data-swatches]')
    ?.querySelectorAll('.swatch')
    .forEach((el) => {
      el.setAttribute('aria-checked', String(el === swatch));
      el.tabIndex = el === swatch ? 0 : -1;
    });

  const img = imageOf(card);
  const source = img ? sourceFor(swatch, img) : null;
  if (source) chosen.set(card, source);
  else chosen.delete(card);
  card.classList.add('has-variant');
  restore(card);

  const variantId = swatch.dataset.variantId;
  if (!variantId) return;
  card.dataset.variantId = variantId;

  // Keep the link's own path and parameters (locale, search tracking); only the variant changes.
  const link = linkOf(card);
  if (!link) return;
  if (!link.dataset.baseHref) link.dataset.baseHref = link.getAttribute('href');
  const url = new URL(swatch.dataset.productUrl || link.dataset.baseHref, window.location.href);
  url.searchParams.set('variant', variantId);
  link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
}

/* Radio group keys: arrows move and select (wrapping, mirrored in RTL), Home/End jump. */
function onSwatchKey(event) {
  const swatch = closest(event.target, SWATCH);
  if (!swatch) return;
  const items = [...(swatch.closest('[data-swatches]')?.querySelectorAll('.swatch') ?? [])];
  const index = items.indexOf(swatch);
  if (index < 0) return;
  const rtl = getComputedStyle(swatch).direction === 'rtl';
  const steps = {
    ArrowDown: 1,
    ArrowUp: -1,
    ArrowRight: rtl ? -1 : 1,
    ArrowLeft: rtl ? 1 : -1,
  };
  let next;
  if (event.key === 'Home') next = 0;
  else if (event.key === 'End') next = items.length - 1;
  else if (event.key in steps) next = (index + steps[event.key] + items.length) % items.length;
  else return;
  event.preventDefault();
  items[next].focus();
  select(items[next]);
}

function leavingSwatch(event) {
  const swatch = closest(event.target, SWATCH);
  if (!swatch) return null;
  const next = closest(event.relatedTarget, SWATCH);
  // Moving to a sibling swatch previews that one instead; skip the restore in between.
  if (next && cardOf(next) === cardOf(swatch)) return null;
  return swatch;
}

/* ---------- Wiring ---------- */

if (!window.Munkwell?.card) {
  window.Munkwell = window.Munkwell || {};
  window.Munkwell.card = { add };

  document.addEventListener('submit', onQuickAddSubmit);

  document.addEventListener('click', (event) => {
    const trigger = closest(event.target, `${CARD} [data-card-quick]`);
    if (trigger) {
      handOff(trigger);
      return;
    }
    const swatch = closest(event.target, SWATCH);
    if (swatch) select(swatch);
  });

  document.addEventListener('pointerover', (event) => {
    if (event.pointerType !== 'mouse') return;
    const swatch = closest(event.target, SWATCH);
    if (swatch) preview(swatch);
  });

  document.addEventListener('pointerout', (event) => {
    if (event.pointerType !== 'mouse') return;
    const swatch = leavingSwatch(event);
    if (swatch && !swatch.contains(event.relatedTarget)) restore(cardOf(swatch));
  });

  document.addEventListener('keydown', onSwatchKey);

  document.addEventListener('focusin', (event) => {
    const swatch = closest(event.target, SWATCH);
    if (swatch) preview(swatch);
  });

  document.addEventListener('focusout', (event) => {
    const swatch = leavingSwatch(event);
    if (swatch) restore(cardOf(swatch));
  });
}
